import { Popover } from "frost-ui-react";
import { PopoverProps } from "frost-ui-react/dist/Popover/Popover";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { formatCurrencySEK, formatNumber } from "../../../utils/date.time.utils";
import { LinkText } from "../../Core/AppText/AppText";
import { ScheduleEvent } from "./Schedule";
import { Flex, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { SalaryData } from "../../../models/ConsultantSchedule";
import Divider from "../Divider/Divider";
import { useConsultantSalarySummary } from "../../../networking/consultant.hooks";
import dayjs from "dayjs";
import { useParams } from "react-router";
import { getNumberValue } from "../../../utils/object.utils";

interface Props {
	scheduleEvent: ScheduleEvent;
	pointTo: PopoverProps["pointTo"];
	onClose: () => any;
}
interface ConsultantsDetailsParamProps {
	consultantId: string;
}
const ShiftSalaryPopover: React.FC<Props> = ({ scheduleEvent, pointTo, onClose }) => {
	if (!scheduleEvent.shift && !scheduleEvent.job) return null;

	const { consultantId } = useParams<
		keyof ConsultantsDetailsParamProps
	>() as ConsultantsDetailsParamProps;
	const { t } = useTranslation();
	const { salary, isLoading } = useConsultantSalarySummary(
		consultantId,
		dayjs(scheduleEvent.start).startOf("day").toISOString(),
		dayjs(scheduleEvent.end).endOf("day").toISOString()
	);

	const tooltipDataList: SalaryData[] = [
		{ label: t("WORKED_HOURS"), figure: salary.workedHours ?? 0, unitType: "hour" },
		{ label: t("BREAK_HOURS"), figure: salary.breakHours ?? 0, unitType: "hour" },
		{ label: t("SICK_LEAVE"), figure: salary.sickHours ?? 0, unitType: "hour" },
		{
			label: "Total",
			figure:
				getNumberValue(salary?.workedHours) -
				(getNumberValue(salary?.breakHours) + getNumberValue(salary.sickHours)),
			unitType: "hour",
		},

		{ label: t("GROSS_SALARY"), figure: salary.grossSalary ?? 0, unitType: "amount" },
		{ label: t("DEDUCTION_AMOUNT"), figure: salary.deduction ?? 0, unitType: "amount" },
		{ label: t("TAX"), figure: salary.tax ?? 0, unitType: "amount" },
		{
			label: t("TO_PAY"),
			figure:
				getNumberValue(salary?.grossSalary) -
				(getNumberValue(salary?.deduction) + getNumberValue(salary.tax)),
			unitType: "amount",
		},
	];

	return (
		<Popover pointTo={pointTo} styles={{ width: 200 }}>
			<Styles>
				<Flex gap="1rem" direction="column">
					<StatItemList isLoading={isLoading} items={tooltipDataList} />
					<LinkText size="xs" onClick={onClose} color="danger" textAlign="center">
						{t("CLOSE")} (Esc)
					</LinkText>
				</Flex>
			</Styles>
		</Popover>
	);
};

export default ShiftSalaryPopover;

const Styles = styled.div`
	.profile-pic {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		border: 2px solid black;
		background: #ccc;
	}
`;

interface StatListProps {
	items: SalaryData[];
	isLoading?: boolean;
}

const StatItemList = ({ isLoading, items }: StatListProps) => {
	return (
		<VStack gap={0} divider={<Divider margin="0" />}>
			{items.map((item, i) => (
				<HStack
					key={i}
					width={"100%"}
					justify={"space-between"}
					borderBottom={(i + 1) % 4 === 0 || (i + 1) % 4 === 3 ? "none" : "1px solid #CCCCCC"}
					pt={2}
					bg={(i + 1) % 4 === 0 ? "#F2F2F2" : ""}
				>
					<Text m={0} fontWeight={400} fontSize={"10px"} color={"#666666"}>
						{item.label}
					</Text>
					{!!isLoading ? (
						<Spinner size="xs" color="#666666" />
					) : (
						<Text m={0} fontWeight={600} fontSize={"10px"} color={"#333333"}>
							{item.unitType === "hour"
								? `${formatNumber(item.figure)} h`
								: formatCurrencySEK(item.figure)}
						</Text>
					)}
				</HStack>
			))}
		</VStack>
	);
};
